import { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { useGetInboxesWithCustom } from '@/api/chat'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { chatTypeToIconMap } from '@/components/Chat/helpers'
import { selectUserData } from '@/reducers/userDataReducer'

import Icon from '../../Icons'

export const renderHeaderIconContainer = (icon) => (
  <div className="header-icon-container">
    <Icon
      icon={icon}
      viewBox={
        icon === 'unified_inbox'
          ? '-5 1 34 21'
          : icon === 'propertymanager_inbox'
            ? '-7 -1 33 21'
            : '-5 0 30 21'
      }
      fill={['propertymanager_inbox', 'unified_inbox'].includes(icon) ? '#fff' : '#141B2C'}
      svgProps={{
        stroke: 'white',
        strokeWidth: ['propertymanager_inbox', 'unified_inbox'].includes(icon) ? '0.3' : '1',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        fillRule: 'evenodd',
      }}
    />
  </div>
)

const InboxSelector = ({ value, onChange, disabled }) => {
  const { t } = useTranslation(['chat'])
  const wrapperRef = useRef(null)
  const [isOpen, setOpen] = useState(false)

  const userData = useSelector(selectUserData)
  const currentBuilding = useSelector(selectActiveBuilding)

  const { data: chatInboxes, getFilteredByRoleInboxes } = useGetInboxesWithCustom({
    buildingId: currentBuilding?.id,
    chatCustom: currentBuilding?.chatCustom,
  })

  const filteredInboxes = getFilteredByRoleInboxes(userData)

  const foundSelectedChat = chatInboxes.find((inbox) => inbox.id === value?.id)
  const hasMoreThanOneOption = filteredInboxes.length > 1

  const selectOption = (inbox) => {
    onChange(inbox)
    setOpen(false)
  }

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setOpen(false)
    }
  }

  const handleOpenSelector = () => {
    if (!hasMoreThanOneOption) return
    setOpen(!isOpen)
  }

  const getInboxPhone = () => {
    if (!chatInboxes) return null
    if (value === 'ALL') {
      return t('chat:inboxSelector.allNumbers')
    }
    return foundSelectedChat?.phone
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  if (!value) return null

  return (
    <div className={`inbox-container ${disabled ? 'loading' : ''}`}>
      <div
        className={`selected-inbox ${hasMoreThanOneOption ? '' : 'one-option'}`}
        onClick={handleOpenSelector}
      >
        {renderHeaderIconContainer(
          value === 'ALL' ? chatTypeToIconMap.ALL : chatTypeToIconMap[foundSelectedChat?.type],
        )}
        <div className="inbox-data">
          <div className="name">
            {value === 'ALL' ? t('chat:inboxSelector.unifiedInbox') : foundSelectedChat?.name}
          </div>
          <div className="number-info">{getInboxPhone()}</div>
        </div>
        {hasMoreThanOneOption && (
          <div className="arrows-container">
            <Icon icon={'arrow'} />
            <Icon icon={'arrow'} />
          </div>
        )}
      </div>
      {isOpen ? (
        <div className={`field-select__dropdown`} ref={wrapperRef}>
          {hasMoreThanOneOption && 'ALL' !== value && (
            <div
              className="field-select__dropdown_wrapper"
              data-value="ALL"
              onClick={() => selectOption('ALL')}
            >
              <div className="field-select__icon-wrapper">
                {renderHeaderIconContainer(chatTypeToIconMap.ALL)}
              </div>
              <span>{t('chat:inboxSelector.unifiedInbox')}</span>
            </div>
          )}
          {filteredInboxes.map((inbox) => {
            if (inbox.id === value?.id) return null

            const iconType = chatTypeToIconMap[inbox.type]

            return (
              <div
                key={inbox.id}
                className="field-select__dropdown_wrapper"
                data-value={inbox.id}
                onClick={() => selectOption(inbox)}
              >
                <div className="field-select__icon-wrapper">
                  {renderHeaderIconContainer(iconType)}
                </div>
                <span>{inbox.name}</span>
              </div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

InboxSelector.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
}

export default InboxSelector
